/// <reference types="../commons.d.ts" preserve="true" />
/* eslint-disable prefer-destructuring */
/**
 * Provides convenience methods and events for DOM functionality.
 *
 * {@include ../lib/debug/README.md}
 * @category Core Modules
 * @module dom
 * @packageDocumentation
 */
import { o_util } from "@gr-common/common/namespaces";
import { type OttoNexus, otto } from "@gr-exports/common/nexus";
import type { DomAsync } from "../lib/dom/index.js";

/**
 * Synchronous interface of the global resources DOM namespace.
 */
export type DomSync = (typeof o_util)["dom"];

export type { DomAsync };

/**
 * Global resources DOM namespace object
 */
export const dom: DomSync = o_util?.dom ?? {};

/**
 * Converts a String containing HTML into a DocumentFragment containing DomNodes
 */
export const stringToDocumentFragment: DomSync["stringToDocumentFragment"] =
  dom.stringToDocumentFragment;
/**
 * Simply returns first element mathing given tag name
 */
export const getFirstElementByTagName: DomSync["getFirstElementByTagName"] =
  dom.getFirstElementByTagName;
/**
 * Returns head element from document
 */
export const getHeadElement: DomSync["getHeadElement"] = dom.getHeadElement;
/**
 * Returns body element from document
 */
export const getBodyElement: DomSync["getBodyElement"] = dom.getBodyElement;

/**
 * Asynchronous interface of the global resources DOM namespace.
 * Exposes functionality of {@link DomAsync} namespace.
 */
export type Dom = OttoNexus["dom"];

/**
 * Event Topic for the `load` event, resolves when the page is fully loaded.
 * The event is retained, thus can be consumed ant any time.
 */
export const onLoad: Dom["onLoad"] = otto.dom?.onLoad;

/**
 * Event Topic for the `DOMContentLoaded` event, resolves when the DOM is fully loaded.
 * The event is retained, thus can be consumed ant any time.
 */
export const onReady: Dom["onReady"] = otto.dom?.onReady;
